import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Upload,
  FileText,
  Users,
  TrendingUp,
  ArrowRight,
  PenTool,
  Loader2,
  Mail,
  Sparkles,
} from 'lucide-react';
import GradeBadge from '@/components/GradeBadge';
import IndividualSetupDialog from '@/components/IndividualSetupDialog';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useUsageCounts } from '@/hooks/useUsageCounts';
import { UNLIMITED } from '@/lib/entitlements';
import { Progress } from '@/components/ui/progress';
import { getCurrentTerm, getTermSeason } from '@/lib/term';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getUpgradeCTA } from '@/lib/upgrade';

interface PendingInvitation {
  id: string;
  school_id: string;
  school_name: string | null;
  invited_at: string;
}

export default function Dashboard() {
  const { profile, hasRole, user, isLoading, schoolPlan, planTier, planSubTier, refreshProfile } = useAuth();
  const isAdmin = hasRole('school_admin');
  const showUsage = planTier === 'free' || planTier === 'individual' || (planTier === 'school' && isAdmin);
  const usage = useUsageCounts({
    userId: user?.id,
    schoolId: profile?.school_id ?? undefined,
    planTier: planTier ?? null,
    planSubTier: planSubTier ?? null,
    isSchoolAdmin: isAdmin,
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [schoolName, setSchoolName] = useState<string | null>(null);
  const [individualSetupOpen, setIndividualSetupOpen] = useState(false);
  const [pendingInvitation, setPendingInvitation] = useState<PendingInvitation | null>(null);
  const [checkingInvitation, setCheckingInvitation] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!profile?.school_id) return;
    supabase
      .from('schools')
      .select('name')
      .eq('id', profile.school_id)
      .single()
      .then(({ data }) => {
        if (data?.name) setSchoolName(data.name);
      });
  }, [profile?.school_id]);

  // Show upgrade success toast when returning from Stripe checkout
  useEffect(() => {
    if (searchParams.get('upgrade') === 'success') {
      toast({ title: 'Plan activated', description: 'Your plan is now active.' });
      setSearchParams({}, { replace: true });
      refreshProfile();
    }
  }, [searchParams, setSearchParams, toast, refreshProfile]);

  // Check for pending school invitation when user has no school yet
  useEffect(() => {
    if (!user?.email || profile?.school_id || isLoading) return;
    setCheckingInvitation(true);
    supabase
      .from('school_invitations')
      .select('id, school_id, schools(name), invited_at')
      .eq('email', user.email.toLowerCase())
      .eq('status', 'pending')
      .maybeSingle()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(({ data }: { data: any }) => {
        if (data) {
          setPendingInvitation({
            id: data.id,
            school_id: data.school_id,
            school_name: data.schools?.name ?? null,
            invited_at: data.invited_at,
          });
        }
        setCheckingInvitation(false);
      });
  }, [user?.email, profile?.school_id, isLoading]);

  const handleAcceptInvitation = async () => {
    if (!pendingInvitation) return;
    setAccepting(true);
    const { data, error } = await supabase.functions.invoke('accept-school-invitation', {});
    setAccepting(false);
    if (error || data?.error) {
      toast({
        title: 'Failed to accept invitation',
        description: error?.message ?? data?.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    await refreshProfile();
    toast({ title: 'Welcome to your school!', description: 'Your account has been linked. Your admin will assign you to classes.' });
    setPendingInvitation(null);
  };

  const isStaff = !hasRole('parent');
  const isSchoolWide = hasRole('school_admin') || hasRole('slt');
  const isSchoolTeacherOnly = planTier === 'school' && !isAdmin;
  const { stats, isLoading: statsLoading } = useDashboardStats(
    profile?.user_id ?? '',
    isSchoolWide,
  );

  // Plan intent stored in Supabase auth user_metadata at sign-up
  const planIntent = user?.user_metadata?.plan_type as 'school' | 'individual' | undefined;

  // User has a pending school invitation (invited teacher who hasn't accepted yet)
  const hasPendingInvitation = !checkingInvitation && !!pendingInvitation && !profile?.school_id;

  const noSchoolYet = !profile?.school_id && !hasPendingInvitation && !checkingInvitation && !isLoading && !!user;

  // New user who chose school plan at sign-up
  const needsSchoolSetup = noSchoolYet && planIntent === 'school';

  // New user who chose individual plan at sign-up
  const needsIndividualSetup = noSchoolYet && planIntent === 'individual';

  // New user who signed up without a plan param (direct /auth) — send to /choose-plan
  const needsPlanChoice = noSchoolYet && !planIntent;

  // Super admins go directly to the admin area (they have no school_id)
  if (hasRole('super_admin') && !isLoading) {
    navigate('/admin/schools', { replace: true });
    return null;
  }

  // Trust leaders go directly to the trust dashboard
  if (hasRole('trust_leader') && !isLoading) {
    navigate('/trust-dashboard', { replace: true });
    return null;
  }

  if (needsPlanChoice) {
    navigate('/choose-plan', { replace: true });
    return null;
  }

  if (needsSchoolSetup) {
    return (
      <DashboardLayout>
        <div className="min-h-[80vh] flex items-center justify-center p-6">
          <Card className="max-w-lg w-full text-center shadow-soft">
            <CardHeader className="pb-4">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <PenTool className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Welcome to WriteAssess!</CardTitle>
              <CardDescription className="text-base">
                Let's set up your school to get started with AI-powered writing assessments.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button asChild size="lg" className="w-full">
                <Link to="/school-setup">
                  Set Up Your School
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Chose the wrong plan?{' '}
                <Link to="/choose-plan" className="text-primary hover:underline">Change it here.</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (hasPendingInvitation) {
    return (
      <DashboardLayout>
        <div className="min-h-[80vh] flex items-center justify-center p-6">
          <Card className="max-w-lg w-full text-center shadow-soft">
            <CardHeader className="pb-4">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">You've been invited!</CardTitle>
              <CardDescription className="text-base">
                You have a pending invitation to join
                {pendingInvitation!.school_name ? (
                  <strong> {pendingInvitation!.school_name}</strong>
                ) : (
                  ' a school'
                )}{' '}
                on WriteAssess.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                size="lg"
                className="w-full"
                onClick={handleAcceptInvitation}
                disabled={accepting}
              >
                {accepting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2 h-4 w-4" />
                )}
                Accept & Join School
              </Button>
              <p className="text-xs text-muted-foreground">
                Your admin will assign you to your classes once you've joined.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (needsIndividualSetup) {
    return (
      <DashboardLayout>
        <div className="min-h-[80vh] flex items-center justify-center p-6">
          <Card className="max-w-lg w-full text-center shadow-soft">
            <CardHeader className="pb-4">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <PenTool className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Welcome to WriteAssess!</CardTitle>
              <CardDescription className="text-base">
                Set up your first class to start assessing your pupils' writing.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button size="lg" className="w-full" onClick={() => setIndividualSetupOpen(true)}>
                Get started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Chose the wrong plan?{' '}
                <Link to="/choose-plan" className="text-primary hover:underline">Change it here.</Link>
              </p>
            </CardContent>
          </Card>
          <IndividualSetupDialog
            open={individualSetupOpen}
            onOpenChange={setIndividualSetupOpen}
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8">
        {/* Welcome header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-foreground">
            Welcome back, {profile?.full_name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-muted-foreground">
            Here's what's happening with your writing assessments
            {planTier === 'school' && schoolName ? ` at ${schoolName}` : ''}.
          </p>
        </div>

        {/* Quick stats */}
        {isStaff && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  This Term's Assessments
                </CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {statsLoading ? '—' : stats?.thisTermAssessments ?? 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {statsLoading ? '' : `In ${getTermSeason(getCurrentTerm())} term`}
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Assessments
                </CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {statsLoading ? '—' : stats?.totalAssessments ?? 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">All time</p>
              </CardContent>
            </Card>

            <Card className="shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Pupils
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {statsLoading ? '—' : stats?.totalPupils ?? 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Across your classes</p>
              </CardContent>
            </Card>

            <Card className="shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Year Groups Covered
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {statsLoading ? '—' : stats?.yearGroupsCovered ?? 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Distinct year groups assessed
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick actions */}
        {isStaff && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="shadow-soft hover:shadow-warm transition-shadow cursor-pointer flex flex-col">
              <Link to="/upload" className="flex flex-col flex-1">
                <CardHeader>
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Upload Writing Sample</CardTitle>
                  <CardDescription>
                    Take a photo or upload an image of pupil work for AI assessment.
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto space-y-3">
                  {showUsage && !usage.loading && usage.maxAssessments !== UNLIMITED && (() => {
                    const pct = Math.min(100, Math.round((usage.assessmentsThisMonth / usage.maxAssessments) * 100));
                    const atLimit = pct >= 100;
                    const nearLimit = pct >= 80;
                    const showUpgradeNudge = (planTier === 'free' || planTier === 'individual') && pct >= 50;
                    const upgradeCta = showUpgradeNudge ? getUpgradeCTA(planTier, planSubTier ?? null) : null;
                    return (
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Assessments this month</span>
                            <span className={atLimit ? 'text-destructive font-medium' : nearLimit ? 'text-amber-600 font-medium' : ''}>
                              {usage.assessmentsThisMonth} / {usage.maxAssessments}
                            </span>
                          </div>
                          <Progress
                            value={pct}
                            className={`h-1.5 ${atLimit ? '[&>div]:bg-destructive' : nearLimit ? '[&>div]:bg-amber-500' : '[&>div]:bg-primary'}`}
                          />
                        </div>
                        {upgradeCta && (
                          <button
                            onClick={(e) => { e.preventDefault(); navigate(upgradeCta.primaryRoute); }}
                            className="flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <Sparkles className="h-3 w-3" />
                            {upgradeCta.primaryLabel}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {isSchoolTeacherOnly && !usage.loading && usage.maxAssessments !== UNLIMITED && usage.assessmentsThisMonth >= usage.maxAssessments && (
                    <p className="text-xs text-destructive">
                      Your school's monthly assessment limit has been reached. Contact your admin.
                    </p>
                  )}
                  <Button variant="secondary" className="w-full">
                    Upload Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Link>
            </Card>

            <Card className="shadow-soft hover:shadow-warm transition-shadow cursor-pointer flex flex-col">
              <Link to="/assessments" className="flex flex-col flex-1">
                <CardHeader>
                  <div className="h-12 w-12 rounded-xl bg-info/10 flex items-center justify-center mb-2">
                    <FileText className="h-6 w-6 text-info" />
                  </div>
                  <CardTitle>View Assessments</CardTitle>
                  <CardDescription>
                    Review AI assessments and provide your professional judgement.
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Button variant="secondary" className="w-full">
                    View All
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Link>
            </Card>

            <Card className="shadow-soft hover:shadow-warm transition-shadow cursor-pointer flex flex-col">
              <Link to="/progress" className="flex flex-col flex-1">
                <CardHeader>
                  <div className="h-12 w-12 rounded-xl bg-success/10 flex items-center justify-center mb-2">
                    <TrendingUp className="h-6 w-6 text-success" />
                  </div>
                  <CardTitle>Track Progress</CardTitle>
                  <CardDescription>
                    View pupil progress over time and identify learning needs.
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Button variant="secondary" className="w-full">
                    View Progress
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Link>
            </Card>

            <Card className="shadow-soft hover:shadow-warm transition-shadow cursor-pointer flex flex-col">
              <Link to="/pupils" className="flex flex-col flex-1">
                <CardHeader>
                  <div className="h-12 w-12 rounded-xl bg-warning/10 flex items-center justify-center mb-2">
                    <Users className="h-6 w-6 text-warning" />
                  </div>
                  <CardTitle>Manage Pupils</CardTitle>
                  <CardDescription>
                    Add, edit and organise pupils across your classes.
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Button variant="secondary" className="w-full">
                    Manage Pupils
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Link>
            </Card>
          </div>
        )}

        {/* Grade legend */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg">Assessment Grades</CardTitle>
            <CardDescription>
              Understanding the National Curriculum assessment levels
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                <GradeBadge grade="working_towards" size="lg" />
                <div>
                  <p className="font-medium">Working Towards</p>
                  <p className="text-sm text-muted-foreground">
                    Pupil is developing skills towards expected standard
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                <GradeBadge grade="expected" size="lg" />
                <div>
                  <p className="font-medium">Expected</p>
                  <p className="text-sm text-muted-foreground">
                    Pupil is meeting the expected standard for their age
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                <GradeBadge grade="greater_depth" size="lg" />
                <div>
                  <p className="font-medium">Greater Depth</p>
                  <p className="text-sm text-muted-foreground">
                    Pupil is exceeding expectations with sophisticated writing
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
